import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type { AddLinkRequest, FileOut } from "@/api/types";

export type { FileOut } from "@/api/types";

export function useFiles(orgSlug: string, projectSlug: string, opts: { enabled?: boolean } = {}) {
  return useQuery<FileOut[], ApiError>({
    queryKey: queryKeys.files(orgSlug, projectSlug),
    enabled: opts.enabled ?? true,
    queryFn: async () =>
      unwrap(await api.GET("/orgs/{slug}/projects/{pslug}/files", { params: { path: { slug: orgSlug, pslug: projectSlug } } })),
  });
}

export function useAddLink(orgSlug: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation<FileOut, ApiError, AddLinkRequest>({
    mutationFn: async (body) =>
      unwrap(
        await api.POST("/orgs/{slug}/projects/{pslug}/links", { params: { path: { slug: orgSlug, pslug: projectSlug } }, body }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.files(orgSlug, projectSlug) }),
  });
}

export function useDeleteFile(orgSlug: string, projectSlug: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      unwrap(
        await api.DELETE("/orgs/{slug}/projects/{pslug}/files/{file_id}", {
          params: { path: { slug: orgSlug, pslug: projectSlug, file_id: id } },
        }),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.files(orgSlug, projectSlug) }),
  });
}

/** Browser-followed URL; the API answers 302 to a short-lived presigned URL (or the link target). */
export function downloadUrl(orgSlug: string, projectSlug: string, id: string): string {
  return `/api/orgs/${encodeURIComponent(orgSlug)}/projects/${encodeURIComponent(projectSlug)}/files/${encodeURIComponent(id)}/download`;
}
